import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { SafeResponsiveContainer } from './SafeResponsiveContainer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, FileText, FileSpreadsheet } from 'lucide-react';

const MOIS_COURTS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const fmt = (n) => (Math.round(n || 0)).toLocaleString('fr-FR');

const StatTile = ({ label, value, sub, color, testId }) => (
  <div data-testid={testId} className={`rounded-lg border p-4 ${color}`}>
    <p className="text-xs font-medium text-gray-600">{label}</p>
    <p className="text-lg font-bold text-gray-900 mt-1">{fmt(value)} FCFA</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export const AnnualDashboard = ({ company, token }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(
        `${API}/companies/${company.id}/annual-summary-${format === 'excel' ? 'excel' : 'pdf'}/${year}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `tableau_bord_annuel_${(company.name || 'entreprise').replace(/\s+/g, '_')}_${year}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Erreur lors de l'export du tableau de bord");
    } finally {
      setExporting(null);
    }
  };

  const fetchSummary = useCallback(async () => {
    if (!company?.id || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/companies/${company.id}/annual-summary/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors du chargement du tableau de bord annuel');
    } finally {
      setLoading(false);
    }
  }, [company?.id, token, year]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  if (!company) return null;

  const totals = data?.totals;
  const chartData = (data?.months || []).map(m => ({
    name: MOIS_COURTS[m.month - 1],
    'Masse brute': m.masse_brute,
    'Net payé': m.masse_nette,
  }));
  const hasData = totals && totals.bulletins > 0;

  return (
    <Card className="bg-white" data-testid="annual-dashboard">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-orange-600" />
              Tableau de bord annuel {year}
            </CardTitle>
            <CardDescription>Masse salariale, CNPS et ITS cumulés mois par mois — {company.name}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="annual-export-pdf"
              disabled={!hasData || exporting !== null} onClick={() => handleExport('pdf')}
              className="text-red-700 border-red-200 hover:bg-red-50">
              {exporting === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              PDF
            </Button>
            <Button variant="outline" size="sm" data-testid="annual-export-excel"
              disabled={!hasData || exporting !== null} onClick={() => handleExport('excel')}
              className="text-green-700 border-green-200 hover:bg-green-50">
              {exporting === 'excel' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
              Excel
            </Button>
            <span className="mx-1 h-5 w-px bg-gray-200" />
            <Button variant="outline" size="sm" data-testid="annual-prev-year" onClick={() => setYear(y => y - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-gray-900 w-14 text-center" data-testid="annual-year-label">{year}</span>
            <Button variant="outline" size="sm" data-testid="annual-next-year" onClick={() => setYear(y => y + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
          </div>
        )}
        {!loading && error && (
          <p className="text-sm text-red-600 py-4" data-testid="annual-error">{error}</p>
        )}
        {!loading && !error && data && !hasData && (
          <p className="text-sm text-gray-500 py-4" data-testid="annual-empty">
            Aucun bulletin enregistré pour {year}.
            {data.available_years?.length > 0 && ` Années disponibles : ${data.available_years.join(', ')}.`}
          </p>
        )}
        {!loading && !error && hasData && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile testId="annual-total-brut" label="Masse salariale brute" value={totals.masse_brute}
                sub={`${totals.bulletins} bulletin(s)`} color="bg-orange-50 border-orange-200" />
              <StatTile testId="annual-total-net" label="Net payé" value={totals.masse_nette}
                color="bg-blue-50 border-blue-200" />
              <StatTile testId="annual-total-cnps" label="CNPS total" value={totals.cnps_total}
                sub={`Salarié ${fmt(totals.cnps_salarie)} • Employeur ${fmt(totals.cnps_employeur)}`}
                color="bg-green-50 border-green-200" />
              <StatTile testId="annual-total-its" label="ITS total" value={totals.its_total}
                sub={`Salarié ${fmt(totals.its_salarie)} • Patronal ${fmt(totals.its_patronal)}`}
                color="bg-purple-50 border-purple-200" />
            </div>

            <div className="h-72">
              <SafeResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
                  <Tooltip formatter={(v) => `${fmt(v)} FCFA`} />
                  <Legend />
                  <Bar dataKey="Masse brute" fill="#ea580c" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Net payé" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </SafeResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="annual-table">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500">
                    <th className="py-2 pr-2">Mois</th>
                    <th className="py-2 pr-2 text-right">Bulletins</th>
                    <th className="py-2 pr-2 text-right">Masse brute</th>
                    <th className="py-2 pr-2 text-right">Net payé</th>
                    <th className="py-2 pr-2 text-right">CNPS salarié</th>
                    <th className="py-2 pr-2 text-right">CNPS employeur</th>
                    <th className="py-2 pr-2 text-right">ITS salarié</th>
                    <th className="py-2 text-right">ITS patronal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.filter(m => m.bulletins > 0).map(m => (
                    <tr key={m.month} className="border-b last:border-0 hover:bg-gray-50" data-testid={`annual-row-${m.month}`}>
                      <td className="py-2 pr-2 font-medium">{MOIS_COURTS[m.month - 1]}</td>
                      <td className="py-2 pr-2 text-right">{m.bulletins}</td>
                      <td className="py-2 pr-2 text-right">{fmt(m.masse_brute)}</td>
                      <td className="py-2 pr-2 text-right">{fmt(m.masse_nette)}</td>
                      <td className="py-2 pr-2 text-right">{fmt(m.cnps_salarie)}</td>
                      <td className="py-2 pr-2 text-right">{fmt(m.cnps_employeur)}</td>
                      <td className="py-2 pr-2 text-right">{fmt(m.its_salarie)}</td>
                      <td className="py-2 text-right">{fmt(m.its_patronal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold bg-gray-50" data-testid="annual-totals-row">
                    <td className="py-2 pr-2">Total {year}</td>
                    <td className="py-2 pr-2 text-right">{totals.bulletins}</td>
                    <td className="py-2 pr-2 text-right">{fmt(totals.masse_brute)}</td>
                    <td className="py-2 pr-2 text-right">{fmt(totals.masse_nette)}</td>
                    <td className="py-2 pr-2 text-right">{fmt(totals.cnps_salarie)}</td>
                    <td className="py-2 pr-2 text-right">{fmt(totals.cnps_employeur)}</td>
                    <td className="py-2 pr-2 text-right">{fmt(totals.its_salarie)}</td>
                    <td className="py-2 text-right">{fmt(totals.its_patronal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
